import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ImportEntity, ImportPlan, ImportVendor } from '../app/models/import';
import { environment } from '../environments/environment';

/**
 * Onboarding imports: load a practice's data out of the software it is leaving.
 *
 * These requests carry a FormData body, so they must NOT set Content-Type: the browser has to write
 * it itself to include the multipart boundary, or the backend will not find the file part.
 */
@Injectable({
  providedIn: 'root'
})
export class ImportService {
  private readonly tenantsUrl = `${environment.api.baseUrl}${environment.api.endpoints.tenants}`;

  constructor(private http: HttpClient) {}

  /** Reads the file and reports what importing it would do. Writes nothing. */
  preview(tenantId: string | number, file: File, vendor: ImportVendor, entity: ImportEntity): Observable<ImportPlan> {
    return this.http.post<ImportPlan>(
      `${this.tenantsUrl}/${tenantId}/imports/preview?${this.query(vendor, entity)}`,
      this.body(file)
    );
  }

  /**
   * Imports the file.
   *
   * @param previewChecksum what the preview returned, so the backend can refuse a different file
   */
  run(
    tenantId: string | number,
    file: File,
    vendor: ImportVendor,
    entity: ImportEntity,
    previewChecksum: string
  ): Observable<ImportPlan> {
    const query = `${this.query(vendor, entity)}&previewChecksum=${encodeURIComponent(previewChecksum)}`;
    return this.http.post<ImportPlan>(
      `${this.tenantsUrl}/${tenantId}/imports?${query}`,
      this.body(file)
    );
  }

  private query(vendor: ImportVendor, entity: ImportEntity): string {
    const params = new URLSearchParams();
    params.set('vendor', vendor);
    params.set('entity', entity);
    return params.toString();
  }

  private body(file: File): FormData {
    const form = new FormData();
    form.append('file', file, file.name);
    return form;
  }
}
